import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as Calender } from '../../assets/icons/clock-icon.svg';
import { ReactComponent as LeftArrow } from '../../assets/icons/left-arrow.svg';
import { convertTo24Hour, getPeriodCurrentTime, parseTime } from '../../shared-functions/DataTime';
import { time12HoursValidation } from '../../shared-functions/Validation';
import MyButton from '../button/Button';

interface CustomDateRangeProperties {
  inputLabel?: string;
  value: string;
  name: string;
  placeholder?: string;
  onChangeValue: (name: string, value: string) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  errorHandle?: boolean;
  errorHandlingFunction?: (value: boolean) => void;
}

/**
 * Custom the Date Picker function
 */
function CustomTimePicker({
  inputLabel,
  name,
  value,
  placeholder,
  onChangeValue,
  valueRequired,
  errorHandlingFunction = () => {},
  readOnly,
  errorHandle,
}: CustomDateRangeProperties) {
  const format: string = '12 hours';
  const reference = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<boolean>(false);

  const [timeValue, setTime] = useState<{
    hours: number;
    minutes: number;
    noon: string;
  }>({
    hours: 0,
    minutes: 0,
    noon: 'AM',
  });

  const [style, setStyle] = useState<any>({});
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [hours, setHours] = useState<number>(
    format === '12 hours' ? new Date().getHours() % 12 || 12 : new Date().getHours(),
  );
  const [minutes, setMinutes] = useState<number>(new Date().getMinutes());
  const [noon, setNoon] = useState<string>(getPeriodCurrentTime());
  /**
   * Handle the Value change
   */
  const handleValueChange = () => {
    onChangeValue(name, convertTo24Hour(`${hours}:${minutes} ${noon}`));
    // setTime(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${noon}`);
    setTime({
      hours,
      minutes,
      noon,
    });
    setOpen(false);
    errorHandlingFunction(true);
  };
  /**
   * Handle the Clear value
   */
  const handleValueClear = () => {
    onChangeValue(name, '');
    setTime({
      hours: 0,
      minutes: 0,
      noon: 'PM',
    });
  };

  /**
   * Handle the time change
   * @param time
   * @param status
   */
  const changeTime = (time: string, status: string) => {
    if (time === 'hours') {
      const changeValue = status === 'add' ? hours + 1 : hours - 1;
      if (
        (format === '12 hours' && changeValue > 0 && changeValue < 13) ||
        (format === '24 hours' && changeValue >= 0 && changeValue < 23)
      ) {
        setHours(changeValue);
      }
    } else if (time === 'minute') {
      const changeValue = status === 'add' ? minutes + 1 : minutes - 1;
      if (changeValue >= 0 && changeValue < 60) {
        setMinutes(changeValue);
      }
    } else if (time === 'noon') {
      setNoon(noon === 'AM' ? 'PM' : 'AM');
    }
  };

  /**
   * Handle the Input click
   */
  const openCalenderView = (event: any) => {
    if (!readOnly) {
      setOpen(true);
      let rect;
      if (event.target.className === 'custom-input-container') {
        rect = event.target.getBoundingClientRect();
      } else {
        rect = event.target.closest('.custom-input-container').getBoundingClientRect();
      }
      if (window.innerHeight > rect.top + rect.height + 200) {
        setStyle({
          top: rect.top + rect.height,
          left: rect.left,
        });
      } else {
        setStyle({
          top: rect.top - 200,
          left: rect.left,
        });
      }
    }
  };

  /**
   * Handle the Input click
   */
  const handleChange = (event: any) => {
    let input = event.target.value.toUpperCase(); // Normalize input to uppercase for AM/PM
    const currentName = event.target.name;
    if (currentName === 'hours' && Number(input) >= 13) {
      input = '12';
    } else if (currentName === 'minutes' && Number(input) >= 60) {
      input = '59';
    } else if (currentName === 'noon' && !['AM', 'PM', 'A', 'P', ''].includes(input)) {
      input = 'AM';
    }

    setTime((previous) => ({
      ...previous,
      [currentName]: input,
    }));
  };

  /**
   * Handle close the calender over flow chlick function
   * @param event
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setOpen(false);
    }
  };
  useEffect((): void => {
    document.addEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (Object.keys(timeValue).length > 0) {
      if (
        time12HoursValidation(
          `${String(timeValue.hours).replace(/^0+/, '').padStart(2, '0')}:${String(timeValue.minutes).replace(/^0+/, '').padStart(2, '0')} ${timeValue.noon}`,
        )
      ) {
        errorHandlingFunction(true);
        onChangeValue(
          name,
          convertTo24Hour(
            `${String(timeValue.hours).replace(/^0+/, '').padStart(2, '0')}:${String(timeValue.minutes).replace(/^0+/, '').padStart(2, '0')} ${timeValue.noon}`,
          ),
        );
        setErrorMessage('');
      } else {
        errorHandlingFunction(false);
        onChangeValue(name, '');
        setErrorMessage('Time format is wrong');
      }
    }
  }, [timeValue]);

  useEffect(() => {
    if (value) {
      const oldValue = value
        ? parseTime(value)
        : {
            hours: 0,
            minutes: 0,
            noon: 'AM',
          };

      setTime(oldValue);
    }
  }, [value]);
  return (
    <div className="custom-input-time-wrap">
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className="custom-time-input">
        <div className="custom-input-container" onClick={openCalenderView} role="presentation">
          {/* <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(event) => handleValueChange(event.target.value)}
            required={valueRequired}
            readOnly
          /> */}
          <span>
            <input
              id="timeInput"
              type="number"
              autoComplete="off"
              name="hours"
              value={
                timeValue.hours ? String(timeValue.hours).replace(/^0+/, '').padStart(2, '0') : '00'
              }
              onChange={handleChange}
              placeholder={placeholder}
              onKeyUp={() => setOpen(false)}
              min={1}
              max={12}
            />
            :
            <input
              id="timeInput"
              type="number"
              autoComplete="off"
              name="minutes"
              value={
                timeValue.minutes
                  ? String(timeValue.minutes).replace(/^0+/, '').padStart(2, '0')
                  : '00'
              }
              onChange={handleChange}
              placeholder={placeholder}
              onKeyUp={() => setOpen(false)}
            />
            <input
              id="timeInput"
              type="text"
              autoComplete="off"
              name="noon"
              value={timeValue.noon}
              onChange={handleChange}
              placeholder={placeholder}
              onKeyUp={() => setOpen(false)}
            />
          </span>
          {/* {format === '12 hours' ? (
            <div className="value-view">
              {value ? convertTo12HourFormat(value) : <p>{placeholder}</p>}
            </div>
          ) : (
            <div className="value-view">{value || <p>{placeholder}</p>}</div>
          )} */}
          <span>
            <Calender />
          </span>
        </div>
        {open &&
          ReactDOM.createPortal(
            <div className="timer-box" ref={reference} style={style}>
              <div className="time-picker">
                <div className="time-picker-body">
                  <div className="time-picker-change-button hours">
                    <div
                      className="top-button"
                      onClick={() => changeTime('hours', 'sub')}
                      role="presentation">
                      <span>
                        <LeftArrow />
                      </span>
                    </div>
                    <div className="text">{hours}</div>
                    <div
                      className="bottom-button"
                      onClick={() => changeTime('hours', 'add')}
                      role="presentation">
                      <span>
                        <LeftArrow />
                      </span>
                    </div>
                  </div>
                  <div className="time-picker-change-button minutes">
                    <div
                      className="top-button"
                      onClick={() => changeTime('minute', 'sub')}
                      role="presentation">
                      <span>
                        <LeftArrow />
                      </span>
                    </div>
                    <div className="text">{minutes}</div>
                    <div
                      className="bottom-button"
                      onClick={() => changeTime('minute', 'add')}
                      role="presentation">
                      <span>
                        <LeftArrow />
                      </span>
                    </div>
                  </div>
                  {format === '12 hours' && (
                    <div className="time-picker-change-button noon">
                      <div
                        className="top-button"
                        onClick={() => changeTime('noon', '')}
                        role="presentation">
                        <span>
                          <LeftArrow />
                        </span>
                      </div>
                      <div className="text">{noon}</div>
                      <div
                        className="bottom-button"
                        onClick={() => changeTime('noon', '')}
                        role="presentation">
                        <span>
                          <LeftArrow />
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="time-picker-footer">
                  <div className="footer-button-left">
                    <div
                      className="close-button text-danger"
                      onClick={handleValueClear}
                      role="presentation">
                      Clear
                    </div>
                  </div>
                  <div className="footer-button-right">
                    <MyButton label="Ok" buttonType="submit" onClickFunc={handleValueChange} />
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
      {errorHandle && errorMessage && <p className="text-danger">{errorMessage}</p>}
    </div>
  );
}

CustomTimePicker.defaultProps = {
  inputLabel: '',
  placeholder: '',
  /**
   * common function
   */
  errorHandlingFunction: () => {},
  valueRequired: false,
  readOnly: false,
  errorHandle: true,
};

export default CustomTimePicker;
